import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import {
    IdInterface,
    OptimalSelectedProductInterface,
    OrderClientInterface,
    OrderClientRelation,
    OrderSignedInterface,
    SelectedProductInterface,
} from '../data';
import { OrderApiRepository } from '../repositories/order-api.repository';
import { ProductCacheRepository } from '../repositories/product-cache.repository';
import { SelectedProductCacheRepository } from '../repositories/selected-product-cache.repository';
import { Transaction } from '../transaction/transaction';

@Injectable()
export class OrderProvider {
    private clearCache = (transaction: Transaction<any>) => {
        if (transaction.isSuccess) {
            this.productCacheRepository.clear().subscribe();
            this.selectedProductCacheRepository.clear().subscribe();
        }
    }

    constructor(
        private orderApiRepository: OrderApiRepository,
        private productCacheRepository: ProductCacheRepository,
        private selectedProductCacheRepository: SelectedProductCacheRepository
    ) {}

    findOptimal(contractCode: IdInterface, product: SelectedProductInterface): Observable<Transaction<OptimalSelectedProductInterface>> {
        return this.orderApiRepository.loadOptimal(contractCode, product);
    }

    findSignatories(product: SelectedProductInterface, clientCode: IdInterface): Observable<Transaction<OrderClientInterface[]>> {
        return this.orderApiRepository.loadSignatories(product, clientCode).pipe(
            map((transaction) => {
                if (transaction.isSuccess) {
                    const countHolders: number = transaction.data.filter(
                        (orderClient) => orderClient.participantRelation === OrderClientRelation.TITULAR
                    ).length;

                    transaction.data.map((orderClient) => {
                        orderClient.isSoleHolder = countHolders === 1;
                    });

                    return Transaction.success(transaction.data);
                } else {
                    return Transaction.error(transaction.error);
                }
            })
        );
    }

    sign(product: SelectedProductInterface): Observable<Transaction<OrderSignedInterface>> {
        return this.orderApiRepository.sign(product).pipe(tap(this.clearCache));
    }

    update(product: SelectedProductInterface): Observable<Transaction<OrderSignedInterface>> {
        return this.orderApiRepository.update(product).pipe(tap(this.clearCache));
    }

    remove(product: SelectedProductInterface): Observable<Transaction<boolean>> {
        return this.orderApiRepository.remove(product).pipe(tap(this.clearCache));
    }

    continue(product: SelectedProductInterface, clientCode: IdInterface): Observable<Transaction<OrderSignedInterface>> {
        return this.orderApiRepository.continue(product, clientCode).pipe(tap(this.clearCache));
    }

    remember(product: SelectedProductInterface, clientCode: IdInterface): Observable<Transaction<boolean>> {
        return this.orderApiRepository.remember(product, clientCode).pipe(tap(this.clearCache));
    }

    acceptSignatories(product: SelectedProductInterface): Observable<Transaction<void>> {
        return this.orderApiRepository.acceptSignatories(product).pipe(tap(this.clearCache));
    }

    documents(product: SelectedProductInterface): Observable<Transaction<any>> {
        return this.orderApiRepository.documents(product);
    }

    upload(product: SelectedProductInterface, document: File): Observable<Transaction<void>> {
        return this.orderApiRepository.upload(product, document);
    }
}
